import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
/** Default ceiling: tall enough to read a persona edit whole, short enough that the actions
 *  below it stay in view on a laptop. */
const DEFAULT_MAX_HEIGHT = 320;
/** Written as an escape on purpose: an invisible literal in source is a trap for the next
 *  reader (and for any tool that trims whitespace). */
const ZERO_WIDTH_SPACE = "\u200b";
/** Per-class paint. Background + left rule carry the signal; the text colour never changes,
 *  so contrast is the ground's contrast in whichever theme is live. */
const LINE_STYLE = {
    // `--- old` / `+++ new` — the preamble naming what is being compared.
    header: {
        color: "var(--ard-text-2)",
        fontWeight: 600,
        borderLeftColor: "transparent",
    },
    // `@@ -a,b +c,d @@` — where in the file the next block sits.
    hunk: {
        color: "var(--ard-text-2)",
        background: "var(--ard-surface-3)",
        borderLeftColor: "var(--ard-border-strong)",
    },
    add: {
        background: "var(--feedback-success-bg)",
        borderLeftColor: "var(--feedback-success-border)",
    },
    del: {
        background: "var(--feedback-error-bg)",
        borderLeftColor: "var(--feedback-error-border)",
    },
    ctx: {
        borderLeftColor: "transparent",
    },
};
export function DiffBlock({ lines, label, maxHeight = DEFAULT_MAX_HEIGHT, className, }) {
    // A diff with no lines is "nothing changed", which is a fact for the caller's caption to
    // state in words — an empty bordered box says nothing and looks broken.
    if (lines.length === 0)
        return null;
    const cls = ["ard-diff", className ?? ""].filter(Boolean).join(" ");
    return (_jsx("pre", { className: cls, 
        // A labelled grouping rather than a landmark: a diff is part of the card it sits in,
        // not a top-level region of the page.
        role: "group", "aria-label": label ?? "Unified diff", 
        // Scrollable content must be reachable without a mouse (WCAG 2.1.1).
        tabIndex: 0, style: {
            margin: 0,
            // Vertical padding only: the per-line fills must bleed to both edges, or an added
            // line reads as a floating bar rather than a highlighted row.
            padding: "var(--space-2) 0",
            maxHeight,
            overflow: "auto",
            border: "1px solid var(--ard-border-strong)",
            borderRadius: "var(--radius-md)",
            background: "var(--ard-surface-2)",
            color: "var(--ard-text)",
            fontFamily: "var(--font-mono)",
            fontSize: "var(--mono-size)",
            fontWeight: 400,
            lineHeight: 1.55,
            // `pre` already means "preserve"; saying it explicitly keeps the rule from being
            // inherited away by a host stylesheet that normalises <pre>.
            whiteSpace: "pre",
            tabSize: 2,
        }, children: lines.map((line, i) => (_jsxs("span", { className: `ard-diff__line ard-diff__line--${line.cls}`, style: {
                display: "block",
                padding: "0 var(--space-3)",
                // Every line carries the rule so the text of every line starts at the same x;
                // only its colour varies (transparent where there is no signal to give).
                borderLeft: "2px solid transparent",
                ...LINE_STYLE[line.cls],
            }, children: [line.text === "" ? ZERO_WIDTH_SPACE : line.text, "\n"] }, i))) }));
}
export default DiffBlock;
//# sourceMappingURL=DiffBlock.js.map